import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';

/**
 * In-grid search state for the Workspace table. Scoped to whatever rows are
 * currently loaded — the Workspace API has no server-side search endpoint
 * (Infynd-FE searches the whole sheet through `POST /workspace/getData`).
 *
 * Matches are tracked per *cell*, not per row, the way Infynd-FE's
 * `searchMatches` does: a row can match in several columns, and the toolbar's
 * n/N counter and the grid's highlighting only line up if each of those is its
 * own stop. Row-level matching also made "next match" skip over hits that were
 * plainly visible, because the row containing them was already current.
 */

export interface WorkspaceSearchMatch {
  rowId: string;
  /** The column key the hit was found in — the row's `input` field name. */
  columnKey: string;
}

interface WorkspaceSearchState {
  query: string;
  matches: WorkspaceSearchMatch[];
  currentMatchIndex: number;
}

const initialState: WorkspaceSearchState = {
  query: '',
  matches: [],
  currentMatchIndex: 0,
};

/**
 * Key for the match lookup below. Joined on a NUL rather than `:` because
 * column keys are raw CSV headers — a header containing a colon would
 * otherwise collide with a different row/column pair. Same separator, and
 * for the same reason, as the hidden-column sync key in WorkspaceDetail.
 */
export const matchKey = (rowId: string, columnKey: string) => `${rowId}\u0000${columnKey}`;

const workspaceSearchSlice = createSlice({
  name: 'workspaceSearch',
  initialState,
  reducers: {
    setSearchQuery(state, action: PayloadAction<string>) {
      state.query = action.payload;
      state.currentMatchIndex = 0;
    },
    setMatches(state, action: PayloadAction<WorkspaceSearchMatch[]>) {
      state.matches = action.payload;
      state.currentMatchIndex = 0;
    },
    nextMatch(state) {
      if (state.matches.length === 0) return;
      state.currentMatchIndex = (state.currentMatchIndex + 1) % state.matches.length;
    },
    prevMatch(state) {
      if (state.matches.length === 0) return;
      state.currentMatchIndex = (state.currentMatchIndex - 1 + state.matches.length) % state.matches.length;
    },
    clearSearch(state) {
      state.query = '';
      state.matches = [];
      state.currentMatchIndex = 0;
    },
  },
});

export const { setSearchQuery, setMatches, nextMatch, prevMatch, clearSearch } = workspaceSearchSlice.actions;

export const selectWorkspaceMatches = (state: RootState) => state.workspaceSearch.matches;

/** The match "next/prev" is currently parked on — the one painted solid. */
export const selectWorkspaceCurrentMatch = (state: RootState): WorkspaceSearchMatch | undefined =>
  state.workspaceSearch.matches[state.workspaceSearch.currentMatchIndex];

/**
 * Every matched cell as a Set, so the grid can ask "is this cell a hit?" in
 * O(1) while rendering rather than scanning the match list per cell.
 * Memoized: this feeds the column-def memo, and a fresh Set each render would
 * rebuild every column definition on every unrelated state change.
 */
export const selectWorkspaceMatchKeySet = createSelector([selectWorkspaceMatches], (matches) => {
  const set = new Set<string>();
  for (const m of matches) set.add(matchKey(m.rowId, m.columnKey));
  return set;
});

export default workspaceSearchSlice.reducer;
